import { PublicClientApplication } from "@azure/msal-browser";
import { jwtDecode } from 'jwt-decode';
import { userStore } from '../stores/userStore'

export const msalConfig = {
    auth: {
        clientId: "d0cd2ed8-0ec6-49e9-b2c6-70e0366a157c",
        authority: "https://login.microsoftonline.com/26dce9f0-a8ed-4298-8698-a1f6655f9111"
    },
    cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false,
    }
};

const pca = new PublicClientApplication(msalConfig);

// Handle successful authentication
async function handleAuthenticationSuccess(tokenResponse: any) {
    const { name, preferred_username } = jwtDecode(tokenResponse.idToken) as {
        name: string,
        preferred_username: string
    };
    const [firstName, ...lastNames] = name.split(' ');
    const lastName = lastNames.join(' ');
    const user = userStore();
    user.setInfo(firstName, lastName, preferred_username, tokenResponse.idToken, tokenResponse.accessToken);
    console.log('Authentication successful for user:', user.lastname);
}

// Get the current active account or prompt for account selection
function getActiveAccount(accounts: any[]) {
    if (!accounts || accounts.length === 0) {
        return null;
    }

    // Try to get the active account from the MSAL instance
    const activeAccount = pca.getActiveAccount();
    if (activeAccount) {
        return activeAccount;
    }

    // If there's only one account, use it
    if (accounts.length === 1) {
        pca.setActiveAccount(accounts[0]);
        return accounts[0];
    }

    // If there are multiple accounts, we should ideally let the user choose
    // For now, we'll use the first one but log a warning
    console.warn('Multiple accounts found, using the first one. Consider implementing account selection.');
    pca.setActiveAccount(accounts[0]);
    return accounts[0];
}

// Login (with popup)
export const login = async () => {
    console.log('Login popup');
    try {
        // First try to get existing accounts
        const accounts = pca.getAllAccounts();
        if (accounts.length > 0) {
            const activeAccount = getActiveAccount(accounts);
            if (activeAccount) {
                // Try silent token acquisition first
                try {
                    const request = {
                        scopes: ["openid", "profile", "User.Read"],
                        account: activeAccount
                    };
                    const tokenResponse = await pca.acquireTokenSilent(request);
                    await handleAuthenticationSuccess(tokenResponse);
                    return;
                } catch (error) {
                    // Silent token acquisition failed, continue with popup
                    console.log('Silent token acquisition failed, trying popup');
                }
            }
        }

        // Initialize MSAL
        try {
            await pca.handleRedirectPromise();
        } catch (error) {
            await pca.initialize();
        }

        // Try popup login
        try {
            const result = await pca.loginPopup({
                scopes: ["openid", "profile", "User.Read"]
            });

            if (result) {
                await handleAuthenticationSuccess(result);
            }
        } catch (popupError) {
            console.error('Popup login failed:', popupError);
            // If popup fails, try redirect
            await pca.loginRedirect({
                scopes: ["openid", "profile", "User.Read"]
            });
        }
    } catch (error) {
        console.error('Login error:', error);
        throw error;
    }
};

// Logout (with popup)
export const logout = () => {
    pca.logoutPopup();
};

// Silent login
export const silentLogin = async () => {
    console.log('Silent login');
    try {
        await pca.handleRedirectPromise();
    } catch (error) {
        await pca.initialize();
    }

    const accounts = pca.getAllAccounts();
    if (accounts.length === 0) {
        console.warn('No accounts found');
        return;
    }

    const activeAccount = getActiveAccount(accounts);
    if (!activeAccount) {
        console.warn('No active account found');
        return;
    }

    const request = {
        scopes: ["openid", "profile", "User.Read"],
        account: activeAccount
    };

    try {
        const tokenResponse = await pca.acquireTokenSilent(request);
        await handleAuthenticationSuccess(tokenResponse);
    } catch (error) {
        console.error('Silent login error:', error);
        throw error;
    }
}
